import { useQuery } from "@tanstack/react-query";

import HomeService from "@/services/HomeService";

// ---- data normalization (join-walks-details endpoint) ----------------------
const extractDetails = (res) => res?.data?.data ?? res?.data ?? {};
const asArray = (v) => (Array.isArray(v) ? v : []);

const buildLocation = (w) =>
    w?.location ||
    [w?.city_name, w?.state_name, w?.country_name].filter(Boolean).join(", ") ||
    "";

// Maps the API's type marker to the join modal's expected value.
const resolveJoinType = (w) => {
    const t = String(w?.type ?? w?.walk_type ?? "").toLowerCase();
    if (t.includes("group") || w?.is_group_walk || w?.group_walk) return "group-walks";
    return "walks";
};

const normalizeWalk = (w = {}, i = 0) => ({
    ...w,
    id: w.id ?? `walk-${i}`,
    name: w.name ?? w.title ?? "",
    description: w.description ?? "",
    location: buildLocation(w),
    image: w.image_url ?? w.logo_url ?? w.image ?? '', //img(`ngud-walk-${w.id ?? i}`),
    joinType: resolveJoinType(w),
});

const normalizeTheme = (t = {}, i = 0) => {
    const walks = asArray(t.walks).map(normalizeWalk);
    // Prefer an explicit count from the backend (the walks array may be
    // partial/paginated); otherwise fall back to the array length.
    const count = Number(
        t.walks_count ?? t.walk_count ?? t.total_walks ?? t.walks_total ?? walks.length
    ) || 0;
    return {
        id: t.id ?? `theme-${i}`,
        title: t.title ?? t.name ?? "",
        phrase: t.phrase ?? t.one_liner ?? t.tagline ?? t.description ?? "",
        image: t.image_url ?? t.image ?? t.logo_url ?? '', //img(`ngud-theme-${t.id ?? i}`),
        walks,
        count,
    };
};

// A walk can legitimately be returned in both `global_walks` and
// `country_walks` (e.g. a country walk that also qualifies as global) —
// dedupe by id so the combined list doesn't produce duplicate React keys.
const dedupeById = (arr) => {
    const seen = new Set();
    return arr.filter((w) => {
        if (w?.id == null) return true;
        if (seen.has(w.id)) return false;
        seen.add(w.id);
        return true;
    });
};

// Normalizes the endpoint payload into the three sections, tolerating a few
// possible key names from the backend.
const normalizeDetails = (data) => {
    const globalCountry = data?.global_country_walks
        ?? dedupeById([...asArray(data?.global_walks), ...asArray(data?.country_walks)]);
    return {
        location: data?.location ?? null,
        themes: asArray(data?.walk_themes).map(normalizeTheme),
        cityWalks: asArray(data?.city_walks ?? data?.cityWalks).map(normalizeWalk),
        globalCountryWalks: asArray(globalCountry).map(normalizeWalk),
    };
};

const useJoinWalksDetails = () =>
    useQuery({
        queryKey: ["join-walks-details"],
        queryFn: () => HomeService.getJoinWalksDetails(),
        select: (res) => normalizeDetails(extractDetails(res)),
        staleTime: 5 * 60 * 1000,
    });

export default useJoinWalksDetails;
